import { Component, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FileService } from '../../../../core/service/file.service';
import { NativeWindowService } from '../../../../core/service/native-window.service';
import { Attachment } from '../../../../common/model/attachment';
import { DialogMessageAttachmentModel } from '../../../../common/model/dialog';
import { PhotoPreviewService } from '../../../../core/service/photo-preview-service';

@Component({
    selector: 'app-chat-message-files',
    templateUrl: './chat-message-files.component.html',
    styleUrls: ['./chat-message-files.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatMessageFilesComponent implements OnInit {
  private _files: DialogMessageAttachmentModel[];
  // Accept base Attachment[] (e.g. deviation/errand attachments) under strictTemplates;
  // previewImage is populated in ngOnInit, so they behave as DialogMessageAttachmentModel.
  @Input() set files(value: Attachment[]) {
    this._files = value as DialogMessageAttachmentModel[];
  }
  get files(): DialogMessageAttachmentModel[] {
    return this._files;
  }

  constructor(
    private readonly _fileService: FileService,
    private readonly _nativeWindow: NativeWindowService,
    private readonly _photoPreviewService: PhotoPreviewService
  ) {}

  ngOnInit(): void {
    this.files.forEach(x => {
      if (!x.previewImage) {
        x.previewImage = this._getPreviewImage(x.data, x.extension);
      }
    });
  }

  previewClick(file: DialogMessageAttachmentModel) {
    if (this._fileService.isWebImage(file.id + '.' + file.extension)) {
      this._photoPreviewService.showPhotoPreview({
        src: file.previewImage,
        fileName: file.id,
      });
    } else {
      this._nativeWindow.save(new File([this._getBlob(file.data, file.extension)], file.id + '.' + file.extension));
    }
  }

  private _getPreviewImage(fileData: Uint8Array, extension: string): string {
    if (fileData?.length === 0) {
      return null;
    }

    switch (extension) {
      case 'png':
      case 'jpeg':
        return this.displayImage(fileData, extension);
      case 'jpg':
      case 'jfif':
        return this.displayImage(fileData, 'jpeg');
      case 'doc':
      case 'docx':
        return '../../../../assets/icons/file_ext/ext_word.svg';
      case 'pdf':
        return '../../../../assets/icons/file_ext/ext_pdf.svg';
      case 'xlsx':
        return '../../../../assets/icons/file_ext/ext_excel.svg';
      default:
        return '../../../../assets/icons/component_icons/not-image-main-icon.png';
    }
  }

  private _getBlob(data: Uint8Array, extension: string) {
    switch (extension) {
      case 'pdf':
        return new Blob([data as BlobPart], { type: 'application/pdf' });
      case 'jpg':
      case 'jfif':
        return new Blob([data as BlobPart], { type: 'image/jpeg' });
      case 'jpeg':
      case 'png':
        return new Blob([data as BlobPart], { type: 'image/' + extension });
      case 'doc':
        return new Blob([data as BlobPart], { type: 'application/msword' });
      case 'docx':
        return new Blob([data as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      case 'xlsx':
        return new Blob([data as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      default:
        null;
    }
  }

  private displayImage(fileBytes: Uint8Array, extension: string): string {
    const blob = this._getBlob(fileBytes, extension);
    const imageUrl = URL.createObjectURL(blob);
    return imageUrl;
  }
}
